import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import { genres, getGenre } from "@/mocks/genres";
import { comics } from "@/mocks/comics";

type SortKey = "popular" | "rating";
type StatusKey = "all" | "连载中" | "已完结";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "popular", label: "人气最高" },
  { key: "rating", label: "评分最高" },
];

export default function Category() {
  const { genre } = useParams<{ genre: string }>();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [sort, setSort] = useState<SortKey>("popular");
  const [status, setStatus] = useState<StatusKey>("all");

  const isAll = genre === "all" || !genre;
  const current = isAll ? null : getGenre(genre!);

  const list = useMemo(() => {
    let base = isAll ? [...comics] : comics.filter((c) => c.genre === genre);
    if (q) {
      base = base.filter(
        (c) => c.title.includes(q) || c.author.includes(q),
      );
    }
    if (status !== "all") {
      base = base.filter((c) => c.status === status);
    }
    if (sort === "popular") {
      base.sort((a, b) => b.views - a.views);
    } else {
      base.sort((a, b) => b.rating - a.rating);
    }
    return base;
  }, [isAll, genre, q, status, sort]);

  const title = q
    ? `“${q}”的搜索结果`
    : isAll
      ? "全部韩漫"
      : `${current?.name}题材韩漫`;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: isAll ? "全部韩漫" : `${current?.name}漫`, to: `/category/${genre}` },
          ]}
        />

        <section
          className={`relative mt-4 overflow-hidden ${
            current ? "bg-gradient-to-br" : "bg-gradient-to-br from-primary-500 to-accent-500"
          } ${current ? current.gradient : ""}`}
        >
          <div className="container-x py-10 md:py-12">
            <div className="flex items-center gap-4">
              {current && (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-background-50/20 text-3xl text-background-50 backdrop-blur-sm md:h-16 md:w-16">
                  <i className={current.icon} />
                </span>
              )}
              <div>
                <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  {title}
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-background-100/90">
                  {q
                    ? `为你找到 ${list.length} 部与“${q}”相关的韩漫作品。`
                    : current
                      ? `${current.desc}，共收录 ${list.length} 部作品。`
                      : "汇总全部题材的热门剧情韩漫，按人气与评分自由筛选，快速找到你想看的那一部。"}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="container-x py-8 md:py-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-full bg-background-100 p-1">
                {(["all", "连载中", "已完结"] as StatusKey[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      status === s
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {s === "all" ? "全部状态" : s}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">排序：</span>
              {sortOptions.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setSort(o.key)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    sort === o.key
                      ? "border-primary-300 bg-primary-100 text-primary-700"
                      : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-dashboard-3-line text-foreground-400" />
            共 <strong className="text-primary-600">{list.length}</strong> 部作品
          </div>

          {list.length > 0 ? (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {list.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center gap-4 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                <i className="ri-inbox-line" />
              </span>
              <p className="text-foreground-600">没有找到符合条件的韩漫，换个关键词试试吧。</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}